import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Avatar,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	InputAdornment,
	InputLabel,
	ListItemText,
	MenuItem,
	Select,
	Stack,
	Tab,
	Tabs,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import SearchIcon from '@mui/icons-material/Search';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import { actualizarCliente, actualizarUsuario, delCliente, delUsuario, getClientes, getUsuarios, postCliente, postUsuario } from '../../../services/apis';

const emptyForm = {
	id: null,
	nombre: '',
	mail: '',
	contrasena: '',
	confirmarContrasena: '',
	id_rol: '',
	id_departamento: '',
	id_cliente: '',
	color: '#1a202c',
	sa: 0,
	tickets1: 0,
	tickets2: 0,
	tickets3: 0,
	tickets4: 0,
};

const emptyClientForm = {
	id: null,
	nombre: '',
	nombre_mostrar: '',
	color_principal: '#1a202c',
	roles_permitidos: ['986'],
	mostrar: 1,
	activo: 1,
	logo: null,
	logoPreview: '',
	eliminar_logo: 0,
};

const ticketLabels = [];

const resizeLogoFile = (file) => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();

		reader.onload = () => {
			const image = new Image();

			image.onload = () => {
				const maxWidth = 300;
				const scale = image.width > maxWidth ? maxWidth / image.width : 1;
				const canvas = document.createElement('canvas');
				canvas.width = Math.round(image.width * scale);
				canvas.height = Math.round(image.height * scale);

				const context = canvas.getContext('2d');
				context.drawImage(image, 0, 0, canvas.width, canvas.height);
				resolve(canvas.toDataURL('image/png'));
			};

			image.onerror = reject;
			image.src = reader.result;
		};

		reader.onerror = reject;
		reader.readAsDataURL(file);
	});
};

const parseRolesPermitidos = (value) => {
	if (Array.isArray(value)) {
		return value.map(String);
	}

	try {
		const parsed = JSON.parse(value || '[]');
		return Array.isArray(parsed) ? parsed.map(String) : [];
	} catch {
		return [];
	}
};

const getClienteId = (cliente) => {
	return cliente?.id ?? cliente?.cliente_id ?? cliente?.id_cliente ?? null;
};

export const Usuarios = () => {
	const [usuarios, setUsuarios] = useState([]);
	const [usuariosClientes, setUsuariosClientes] = useState([]);
	const [roles, setRoles] = useState([]);
	const [departamentos, setDepartamentos] = useState([]);
	const [clientes, setClientes] = useState([]);
	const [clientesCrud, setClientesCrud] = useState([]);
	const [clientesLoaded, setClientesLoaded] = useState(false);
	const [loaded, setLoaded] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	const [success, setSuccess] = useState('');
	const [query, setQuery] = useState('');
	const [clientQuery, setClientQuery] = useState('');
	const [tab, setTab] = useState('todos');
	const [dialogOpen, setDialogOpen] = useState(false);
	const [confirmDelete, setConfirmDelete] = useState(null);
	const [form, setForm] = useState(emptyForm);
	const [clientDialogOpen, setClientDialogOpen] = useState(false);
	const [clientForm, setClientForm] = useState(emptyClientForm);
	const [confirmClientDelete, setConfirmClientDelete] = useState(null);

	const cargarUsuarios = useCallback(async () => {
		setLoaded(false);
		setError('');

		try {
			const response = await getUsuarios();

			if (response?.success) {
				setUsuarios(response.data.usuarios || []);
				setUsuariosClientes(response.data.usuarios_clientes || []);
				setRoles(response.data.roles || []);
				setDepartamentos(response.data.departamentos || []);
				setClientes(response.data.clientes || []);
			} else {
				setError(response?.message || 'No se han podido cargar los usuarios.');
			}
		} catch (err) {
			console.error("Error al cargar usuarios:", err);
			setError('Error al cargar usuarios.');
		} finally {
			setLoaded(true);
		}
	}, []);

	const cargarClientes = useCallback(async () => {
		setClientesLoaded(false);

		try {
			const response = await getClientes();

			if (response?.success) {
				setClientesCrud(response.data || []);
				setClientes(response.data || []);
			}
		} catch (err) {
			console.error("Error al cargar clientes:", err);
			setError('Error al cargar clientes.');
		} finally {
			setClientesLoaded(true);
		}
	}, []);

	useEffect(() => {
		cargarUsuarios();
		cargarClientes();
	}, [cargarUsuarios, cargarClientes]);

	const allUsers = useMemo(() => {
		return [...usuarios, ...usuariosClientes].map((usuario) => ({
			...usuario,
			id_rol: usuario.id_rol || usuario.rol || '',
			id_departamento: usuario.id_departamento || usuario.departamento || '',
			tickets1: Number(usuario.tickets1 || 0),
			tickets2: Number(usuario.tickets2 || 0),
			tickets3: Number(usuario.tickets3 || 0),
			tickets4: Number(usuario.tickets4 || 0),
		}));
	}, [usuarios, usuariosClientes]);

	const stats = useMemo(() => {
		const usuariosEspeciales = allUsers.filter((usuario) => !usuario.id_cliente).length;
		const clientesCount = allUsers.filter((usuario) => usuario.id_cliente).length;

		return {
			total: allUsers.length,
			usuariosEspeciales,
			clientes: clientesCount,
			roles: roles.length,
			clientesRegistrados: clientesCrud.length,
		};
	}, [allUsers, roles.length, clientesCrud.length]);

	const filteredUsers = useMemo(() => {
		const text = query.trim().toLowerCase();

		return allUsers.filter((usuario) => {
			const typeMatch =
				tab === 'todos' ||
				(tab === 'usuarios-especiales' && !usuario.id_cliente) ||
				(tab === 'clientes' && usuario.id_cliente);

			if (!typeMatch) {
				return false;
			}

			if (!text) {
				return true;
			}

			const clienteUsuario = clientes.find((cliente) => String(getClienteId(cliente)) === String(usuario.id_cliente));

			return [
				usuario.nombre,
				usuario.mail,
				usuario.nombre_rol,
				usuario.nombre_departamento,
				usuario.nombre_cliente,
				clienteUsuario?.nombre,
				clienteUsuario?.nombre_mostrar,
			].some((value) => String(value || '').toLowerCase().includes(text));
		});
	}, [allUsers, clientes, query, tab]);

	const getRoleName = (id) => {
		return roles.find((rol) => String(rol.id) === String(id))?.nombre || 'Sin rol';
	};

	const getDepartmentName = (id) => {
		return departamentos.find((departamento) => String(departamento.id) === String(id))?.nombre || 'Sin departamento';
	};

	const getClientName = (id) => {
		const cliente = clientes.find((item) => String(getClienteId(item)) === String(id));
		return cliente?.nombre_mostrar || cliente?.nombre || '';
	};

	const getRoleNamesFromValue = (value) => {
		const selectedRoles = parseRolesPermitidos(value);
		const roleNames = selectedRoles.map((id) => getRoleName(id)).filter(Boolean);

		return roleNames.length > 0 ? roleNames.join(', ') : 'Sin roles';
	};

	const filteredClients = useMemo(() => {
		const text = clientQuery.trim().toLowerCase();

		if (!text) {
			return clientesCrud;
		}

		return clientesCrud.filter((cliente) => {
			return [
				cliente.nombre,
				cliente.nombre_mostrar,
			].some((value) => String(value || '').toLowerCase().includes(text));
		});
	}, [clientesCrud, clientQuery]);

	const openCreateDialog = () => {
		setForm(emptyForm);
		setError('');
		setSuccess('');
		setDialogOpen(true);
	};

	const openEditDialog = (usuario) => {
		setForm({
			...emptyForm,
			id: usuario.id,
			nombre: usuario.nombre || '',
			mail: usuario.mail || '',
			id_rol: usuario.rol || usuario.id_rol || '',
			id_departamento: usuario.departamento || usuario.id_departamento || '',
			id_cliente: usuario.id_cliente || '',
			color: usuario.color || '#1a202c',
			sa: Number(usuario.sa || 0),
			tickets1: Number(usuario.tickets1 || 0),
			tickets2: Number(usuario.tickets2 || 0),
			tickets3: Number(usuario.tickets3 || 0),
			tickets4: Number(usuario.tickets4 || 0),
		});
		setError('');
		setSuccess('');
		setDialogOpen(true);
	};

	const closeDialog = () => {
		if (!saving) {
			setDialogOpen(false);
			setForm(emptyForm);
		}
	};

	const handleFormChange = (event) => {
		const { name, value } = event.target;
		setForm((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleTicketChange = (event) => {
		const { name, value } = event.target;
		setForm((prev) => ({
			...prev,
			[name]: Math.max(0, Number(value || 0)),
		}));
	};

	const validateForm = () => {
		if (!form.nombre.trim() || !form.mail.trim() || !form.id_rol || !form.id_departamento || !form.color) {
			return 'Completa nombre, mail, rol, departamento y color.';
		}

		if (!form.id && !form.contrasena) {
			return 'La contraseña es obligatoria al crear un usuario.';
		}

		if (form.contrasena !== form.confirmarContrasena) {
			return 'Las contraseñas no coinciden.';
		}

		return '';
	};

	const buildPayload = () => ({
		id: form.id,
		nombre: form.nombre.trim(),
		mail: form.mail.trim(),
		id_rol: form.id_rol,
		id_departamento: form.id_departamento,
		id_cliente: form.id_cliente || null,
		color: form.color,
		sa: Number(form.sa || 0),
		contrasena: form.contrasena,
		tickets1: Number(form.tickets1 || 0),
		tickets2: Number(form.tickets2 || 0),
		tickets3: Number(form.tickets3 || 0),
		tickets4: Number(form.tickets4 || 0),
	});

	const handleSubmit = async (event) => {
		event.preventDefault();
		const validationMessage = validateForm();

		if (validationMessage) {
			setError(validationMessage);
			return;
		}

		setSaving(true);
		setError('');
		setSuccess('');

		try {
			const payload = buildPayload();
			const response = form.id ? await actualizarUsuario(payload) : await postUsuario(payload);

			if (response?.success) {
				setSuccess(form.id ? 'Usuario actualizado correctamente.' : 'Usuario creado correctamente.');
				setDialogOpen(false);
				setForm(emptyForm);
				await cargarUsuarios();
			} else {
				setError(response?.error || response?.message || 'No se ha podido guardar el usuario.');
			}
		} catch (err) {
			console.error("Error al guardar usuario:", err);
			setError('Error al guardar usuario.');
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async () => {
		if (!confirmDelete) {
			return;
		}

		setSaving(true);
		setError('');
		setSuccess('');

		try {
			const response = await delUsuario(confirmDelete.id);

			if (response?.success) {
				setSuccess('Usuario eliminado correctamente.');
				setConfirmDelete(null);
				await cargarUsuarios();
			} else {
				setError(response?.error || response?.message || 'No se ha podido eliminar el usuario.');
			}
		} catch (err) {
			console.error("Error al eliminar usuario:", err);
			setError('Error al eliminar usuario.');
		} finally {
			setSaving(false);
		}
	};

	const openCreateClientDialog = () => {
		setClientForm(emptyClientForm);
		setError('');
		setSuccess('');
		setClientDialogOpen(true);
	};

	const openEditClientDialog = (cliente) => {
		const clienteId = getClienteId(cliente);

		setClientForm({
			...emptyClientForm,
			id: clienteId,
			nombre: cliente.nombre || '',
			nombre_mostrar: cliente.nombre_mostrar || '',
			color_principal: cliente.color_principal || '#1a202c',
			roles_permitidos: parseRolesPermitidos(cliente.roles_permitidos || '[986]'),
			mostrar: Number(cliente.mostrar ?? 1),
			activo: Number(cliente.activo ?? 1),
			logo: null,
			logoPreview: cliente.logo || '',
			eliminar_logo: 0,
		});
		setError('');
		setSuccess('');
		setClientDialogOpen(true);
	};

	const closeClientDialog = () => {
		if (!saving) {
			setClientDialogOpen(false);
			setClientForm(emptyClientForm);
		}
	};

	const handleClientFormChange = (event) => {
		const { name, value } = event.target;
		setClientForm((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleClientNumberChange = (event) => {
		const { name, value } = event.target;
		setClientForm((prev) => ({
			...prev,
			[name]: Number(value),
		}));
	};

	const handleClientRolesChange = (event) => {
		const value = event.target.value;
		setClientForm((prev) => ({
			...prev,
			roles_permitidos: typeof value === 'string' ? value.split(',') : value.map(String),
		}));
	};

	const handleLogoChange = async (event) => {
		const file = event.target.files?.[0];

		if (!file) {
			return;
		}

		if (!file.type.startsWith('image/')) {
			setError('El logo debe ser una imagen.');
			return;
		}

		try {
			const resizedLogo = await resizeLogoFile(file);
			setClientForm((prev) => ({
				...prev,
				logo: resizedLogo,
				logoPreview: resizedLogo,
				eliminar_logo: 0,
			}));
		} catch (err) {
			console.error("Error al procesar logo:", err);
			setError('No se ha podido procesar el logo.');
		}
	};

	const removeClientLogo = () => {
		setClientForm((prev) => ({
			...prev,
			logo: '',
			logoPreview: '',
			eliminar_logo: prev.id ? 1 : 0,
		}));
	};

	const buildClientPayload = () => ({
		id: clientForm.id,
		nombre: clientForm.nombre.trim(),
		nombre_mostrar: clientForm.nombre_mostrar.trim(),
		color_principal: clientForm.color_principal,
		roles_permitidos: JSON.stringify(clientForm.roles_permitidos || []),
		mostrar: Number(clientForm.mostrar),
		activo: Number(clientForm.activo),
		logo: clientForm.logo,
		eliminar_logo: Number(clientForm.eliminar_logo),
	});

	const handleClientSubmit = async (event) => {
		event.preventDefault();
		const isEditingClient = clientForm.id !== null && clientForm.id !== undefined && clientForm.id !== '';

		if (!clientForm.nombre.trim()) {
			setError('El nombre del cliente es obligatorio.');
			return;
		}

		setSaving(true);
		setError('');
		setSuccess('');

		try {
			const payload = buildClientPayload();
			const response = isEditingClient ? await actualizarCliente(payload) : await postCliente(payload);

			if (response?.success) {
				setSuccess(isEditingClient ? 'Cliente actualizado correctamente.' : 'Cliente creado correctamente.');
				setClientDialogOpen(false);
				setClientForm(emptyClientForm);
				await cargarClientes();
				await cargarUsuarios();
			} else {
				setError(response?.error || response?.message || 'No se ha podido guardar el cliente.');
			}
		} catch (err) {
			console.error("Error al guardar cliente:", err);
			setError('Error al guardar cliente.');
		} finally {
			setSaving(false);
		}
	};

	const handleClientDelete = async () => {
		if (!confirmClientDelete) {
			return;
		}

		setSaving(true);
		setError('');
		setSuccess('');

		try {
			const response = await delCliente(getClienteId(confirmClientDelete));

			if (response?.success) {
				setSuccess('Cliente eliminado correctamente.');
				setConfirmClientDelete(null);
				await cargarClientes();
				await cargarUsuarios();
			} else {
				setError(response?.error || response?.message || 'No se ha podido eliminar el cliente.');
			}
		} catch (err) {
			console.error("Error al eliminar cliente:", err);
			setError('Error al eliminar cliente.');
		} finally {
			setSaving(false);
		}
	};

	return (
		<Box className="users-shell fade-in-2">
			<Box className="users-header">
				<Box className="users-header-copy">
					<Typography className="users-eyebrow">Administración</Typography>
					<Typography variant="h4" className="users-title">
						Usuarios
					</Typography>
				</Box>

				<Stack className="users-toolbar" direction="row">
					<Chip className="users-stat-chip" icon={<GroupsOutlinedIcon />} label={`${stats.total} usuarios`} />
					<Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
						Nuevo usuario
					</Button>
				</Stack>
			</Box>

			{error && (
				<Alert severity="error" className="users-alert" onClose={() => setError('')}>
					{error}
				</Alert>
			)}

			{success && (
				<Alert severity="success" className="users-alert" onClose={() => setSuccess('')}>
					{success}
				</Alert>
			)}

			<Box className="users-summary-grid">
				<Box className="users-summary-card">
					<Typography>{stats.usuariosEspeciales}</Typography>
					<span>Usuarios especiales</span>
				</Box>
				<Box className="users-summary-card">
					<Typography>{stats.clientes}</Typography>
					<span>Usuarios cliente</span>
				</Box>
				<Box className="users-summary-card">
					<Typography>{stats.clientesRegistrados}</Typography>
					<span>Clientes</span>
				</Box>
				<Box className="users-summary-card">
					<Typography>{stats.roles}</Typography>
					<span>Roles</span>
				</Box>
			</Box>

			<Box className="users-content-grid">
			<Box className="users-panel">
				<Box className="users-filters">
					<Tabs value={tab} onChange={(_, value) => setTab(value)} className="users-tabs">
						<Tab value="todos" label="Todos" />
						<Tab value="usuarios-especiales" label="Usuarios especiales" />
						<Tab value="clientes" label="Clientes" />
					</Tabs>

					<TextField
						className="users-search"
						value={query}
						onChange={(event) => setQuery(event.target.value)}
						size="small"
						placeholder="Buscar usuario, mail, rol..."
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon />
								</InputAdornment>
							),
						}}
					/>
				</Box>

				{!loaded && (
					<Box className="users-loading">
						<CircularProgress size={32} />
					</Box>
				)}

				{loaded && (
					<Box className="users-table-wrap">
						<Table className="users-table">
							<TableHead>
								<TableRow>
									<TableCell>Usuario</TableCell>
									<TableCell>Rol</TableCell>
									<TableCell>Departamento</TableCell>
									<TableCell>Tipo</TableCell>
									<TableCell>Tickets</TableCell>
									<TableCell align="right">Acciones</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{filteredUsers.map((usuario) => (
									<TableRow key={usuario.id}>
										<TableCell>
											<Box className="users-user-cell">
												<Avatar className="users-avatar" style={{ backgroundColor: usuario.color || '#1a202c' }}>
													{String(usuario.nombre || 'U').slice(0, 1).toUpperCase()}
												</Avatar>
												<Box className="users-user-copy">
													<Typography className="users-user-name">{usuario.nombre}</Typography>
													<span><EmailOutlinedIcon /> {usuario.mail || 'Sin mail'}</span>
												</Box>
											</Box>
										</TableCell>
										<TableCell>
											<Chip className="users-soft-chip" label={usuario.nombre_rol || getRoleName(usuario.rol)} />
										</TableCell>
										<TableCell>{usuario.nombre_departamento || getDepartmentName(usuario.departamento)}</TableCell>
										<TableCell>
											{usuario.id_cliente ? (
												<Chip className="users-client-chip" label={usuario.nombre_cliente || getClientName(usuario.id_cliente)} />
											) : (
												<Chip className="users-special-user-chip" label="Usuario especial" />
											)}
										</TableCell>
										<TableCell>
											<Box className="users-ticket-row">
												{ticketLabels.map((ticket) => (
													<span key={ticket.key}>{usuario[ticket.key] || 0}</span>
												))}
											</Box>
										</TableCell>
										<TableCell align="right">
											<Tooltip title="Editar">
												<IconButton className="users-icon-button" onClick={() => openEditDialog(usuario)}>
													<EditIcon />
												</IconButton>
											</Tooltip>
											<Tooltip title="Eliminar">
												<IconButton className="users-icon-button users-danger-button" onClick={() => setConfirmDelete(usuario)}>
													<DeleteForeverIcon />
												</IconButton>
											</Tooltip>
										</TableCell>
									</TableRow>
								))}

								{filteredUsers.length === 0 && (
									<TableRow>
										<TableCell colSpan={6}>
											<Box className="users-empty">
												<GroupsOutlinedIcon />
												<Typography>No hay usuarios con estos filtros.</Typography>
											</Box>
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</Box>
				)}
			</Box>

			<Box className="clients-panel">
				<Box className="clients-panel-header">
					<Box>
						<Typography className="users-eyebrow">Clientes</Typography>
						<Typography variant="h5" className="clients-title">
							Clientes registrados
						</Typography>
					</Box>
					<Stack className="clients-toolbar" direction="row">
						<TextField
							className="clients-search"
							value={clientQuery}
							onChange={(event) => setClientQuery(event.target.value)}
							size="small"
							placeholder="Buscar cliente..."
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon />
									</InputAdornment>
								),
							}}
						/>
						<Button variant="contained" startIcon={<AddIcon />} onClick={openCreateClientDialog}>
							Nuevo cliente
						</Button>
					</Stack>
				</Box>

				{!clientesLoaded && (
					<Box className="users-loading">
						<CircularProgress size={30} />
					</Box>
				)}

				{clientesLoaded && (
					<Box className="clients-grid">
						{filteredClients.map((cliente) => (
							<Box key={getClienteId(cliente)} className="client-card">
								<Box className="client-card-top">
									<Box className="client-logo-frame">
										{cliente.logo ? (
											<img src={cliente.logo} alt={cliente.nombre} />
										) : (
											<ImageOutlinedIcon />
										)}
									</Box>
									<Box className="client-card-copy">
										<Typography className="client-name">{cliente.nombre_mostrar || cliente.nombre}</Typography>
										<span>{cliente.nombre}</span>
									</Box>
									<Chip
										className={Number(cliente.activo) === 1 ? 'client-active-chip' : 'client-inactive-chip'}
										label={Number(cliente.activo) === 1 ? 'Activo' : 'Inactivo'}
									/>
								</Box>

								<Box className="client-actions">
									<Button variant="outlined" startIcon={<EditIcon />} onClick={() => openEditClientDialog(cliente)}>
										Editar
									</Button>
									<Button color="error" startIcon={<DeleteForeverIcon />} onClick={() => setConfirmClientDelete(cliente)}>
										Eliminar
									</Button>
								</Box>
							</Box>
						))}

						{filteredClients.length === 0 && (
							<Box className="users-empty">
								<GroupsOutlinedIcon />
								<Typography>No hay clientes con estos filtros.</Typography>
							</Box>
						)}
					</Box>
				)}
			</Box>
			</Box>

			<Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
				<form onSubmit={handleSubmit}>
					<DialogTitle className="users-dialog-title">
						<span>{form.id ? 'Editar usuario' : 'Nuevo usuario'}</span>
						<IconButton onClick={closeDialog} disabled={saving}>
							<CloseIcon />
						</IconButton>
					</DialogTitle>

					<DialogContent className="users-dialog-content">
						<Box className="users-form-grid">
							<Box className="users-form-identity">
								<Avatar className="users-form-avatar" style={{ backgroundColor: form.color }}>
									{String(form.nombre || 'U').slice(0, 1).toUpperCase()}
								</Avatar>
								<Box className="users-color-control">
									<PaletteOutlinedIcon />
									<input type="color" name="color" value={form.color} onChange={handleFormChange} />
								</Box>
							</Box>

							<TextField label="Nombre" name="nombre" value={form.nombre} onChange={handleFormChange} required fullWidth />
							<TextField label="Mail" name="mail" value={form.mail} onChange={handleFormChange} type="email" required fullWidth />

							<FormControl fullWidth required>
								<InputLabel>Rol</InputLabel>
								<Select name="id_rol" value={form.id_rol} label="Rol" onChange={handleFormChange}>
									{roles.map((rol) => (
										<MenuItem key={rol.id} value={rol.id}>
											{rol.nombre}
										</MenuItem>
									))}
								</Select>
							</FormControl>

							<FormControl fullWidth required>
								<InputLabel>Departamento</InputLabel>
								<Select name="id_departamento" value={form.id_departamento} label="Departamento" onChange={handleFormChange}>
									{departamentos.map((departamento) => (
										<MenuItem key={departamento.id} value={departamento.id}>
											{departamento.nombre}
										</MenuItem>
									))}
								</Select>
							</FormControl>

							<FormControl fullWidth>
								<InputLabel>Cliente</InputLabel>
								<Select name="id_cliente" value={form.id_cliente} label="Cliente" onChange={handleFormChange}>
									<MenuItem value="">Sin cliente</MenuItem>
									{clientes.map((cliente) => (
										<MenuItem key={getClienteId(cliente)} value={getClienteId(cliente)}>
											{cliente.nombre_mostrar || cliente.nombre}
										</MenuItem>
									))}
								</Select>
							</FormControl>

							<TextField
								label={form.id ? 'Nueva contraseña' : 'Contraseña'}
								name="contrasena"
								value={form.contrasena}
								onChange={handleFormChange}
								type="password"
								required={!form.id}
								fullWidth
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<VpnKeyOutlinedIcon />
										</InputAdornment>
									),
								}}
							/>
							<TextField
								label="Confirmar contraseña"
								name="confirmarContrasena"
								value={form.confirmarContrasena}
								onChange={handleFormChange}
								type="password"
								required={!form.id || Boolean(form.contrasena)}
								fullWidth
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<VpnKeyOutlinedIcon />
										</InputAdornment>
									),
								}}
							/>
						</Box>

						<Box className="users-ticket-editor">
							<Box className="users-ticket-editor-title">
								<BadgeOutlinedIcon />
								<Typography>Tickets</Typography>
							</Box>
							<Box className="users-ticket-editor-grid">
								{ticketLabels.map((ticket) => (
									<TextField
										key={ticket.key}
										label={ticket.label}
										name={ticket.key}
										value={form[ticket.key]}
										onChange={handleTicketChange}
										type="number"
										inputProps={{ min: 0 }}
									/>
								))}
							</Box>
						</Box>
					</DialogContent>

					<DialogActions className="users-dialog-actions">
						<Button onClick={closeDialog} disabled={saving}>
							Cancelar
						</Button>
						<Button type="submit" variant="contained" disabled={saving}>
							{form.id ? 'Guardar cambios' : 'Crear usuario'}
						</Button>
					</DialogActions>
				</form>
			</Dialog>

			<Dialog open={clientDialogOpen} onClose={closeClientDialog} maxWidth="md" fullWidth>
				<form onSubmit={handleClientSubmit}>
					<DialogTitle className="users-dialog-title">
						<span>{clientForm.id !== null && clientForm.id !== undefined && clientForm.id !== '' ? 'Editar cliente' : 'Nuevo cliente'}</span>
						<IconButton onClick={closeClientDialog} disabled={saving}>
							<CloseIcon />
						</IconButton>
					</DialogTitle>

					<DialogContent className="users-dialog-content">
						<Box className="client-form-grid">
							<Box className="client-logo-editor">
								<Box className="client-logo-preview">
									{clientForm.logoPreview ? (
										<img src={clientForm.logoPreview} alt="Logo del cliente" />
									) : (
										<ImageOutlinedIcon />
									)}
								</Box>
								<Stack className="client-logo-actions" direction="row">
									<Button variant="outlined" component="label" startIcon={<ImageOutlinedIcon />}>
										Subir logo
										<input type="file" hidden accept="image/*" onChange={handleLogoChange} />
									</Button>
									<Button color="error" onClick={removeClientLogo} disabled={!clientForm.logoPreview}>
										Quitar
									</Button>
								</Stack>
								<Typography className="client-logo-help">
									Se guardará en base64 con un ancho máximo de 300px.
								</Typography>
							</Box>

							<TextField label="Nombre" name="nombre" value={clientForm.nombre} onChange={handleClientFormChange} required fullWidth />
							<TextField label="Nom penya" name="nombre_mostrar" value={clientForm.nombre_mostrar} onChange={handleClientFormChange} fullWidth />

							<Box className="client-color-field">
								<PaletteOutlinedIcon />
								<TextField label="Color principal" name="color_principal" value={clientForm.color_principal} onChange={handleClientFormChange} fullWidth />
								<input type="color" name="color_principal" value={clientForm.color_principal} onChange={handleClientFormChange} />
							</Box>

							<FormControl fullWidth>
								<InputLabel>Roles permitidos</InputLabel>
								<Select
									multiple
									name="roles_permitidos"
									value={clientForm.roles_permitidos}
									label="Roles permitidos"
									onChange={handleClientRolesChange}
									renderValue={(selected) => selected.map((id) => getRoleName(id)).join(', ')}
								>
									{roles.map((rol) => (
										<MenuItem key={rol.id} value={String(rol.id)}>
											<Checkbox checked={clientForm.roles_permitidos.includes(String(rol.id))} />
											<ListItemText primary={rol.nombre} />
										</MenuItem>
									))}
								</Select>
							</FormControl>

							<FormControl fullWidth>
								<InputLabel>Activo</InputLabel>
								<Select name="activo" value={clientForm.activo} label="Activo" onChange={handleClientNumberChange}>
									<MenuItem value={1}>Activo</MenuItem>
									<MenuItem value={0}>Inactivo</MenuItem>
								</Select>
							</FormControl>

							<FormControl fullWidth>
								<InputLabel>Mostrar</InputLabel>
								<Select name="mostrar" value={clientForm.mostrar} label="Mostrar" onChange={handleClientNumberChange}>
									<MenuItem value={1}>Visible</MenuItem>
									<MenuItem value={0}>Oculto</MenuItem>
								</Select>
							</FormControl>
						</Box>
					</DialogContent>

					<DialogActions className="users-dialog-actions">
						<Button onClick={closeClientDialog} disabled={saving}>
							Cancelar
						</Button>
						<Button type="submit" variant="contained" disabled={saving}>
							{clientForm.id !== null && clientForm.id !== undefined && clientForm.id !== '' ? 'Guardar cambios' : 'Crear cliente'}
						</Button>
					</DialogActions>
				</form>
			</Dialog>

			<Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
				<DialogTitle>Eliminar usuario</DialogTitle>
				<DialogContent>
					<Typography>
						Vas a eliminar a <strong>{confirmDelete?.nombre}</strong>. Esta acción ocultará el usuario de la app.
					</Typography>
				</DialogContent>
				<DialogActions className="users-dialog-actions">
					<Button onClick={() => setConfirmDelete(null)} disabled={saving}>
						Cancelar
					</Button>
					<Button variant="contained" color="error" onClick={handleDelete} disabled={saving}>
						Eliminar
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog open={Boolean(confirmClientDelete)} onClose={() => setConfirmClientDelete(null)} maxWidth="xs" fullWidth>
				<DialogTitle>Eliminar cliente</DialogTitle>
				<DialogContent>
					<Typography>
						Vas a eliminar <strong>{confirmClientDelete?.nombre}</strong>. El cliente se ocultará y quedará inactivo.
					</Typography>
				</DialogContent>
				<DialogActions className="users-dialog-actions">
					<Button onClick={() => setConfirmClientDelete(null)} disabled={saving}>
						Cancelar
					</Button>
					<Button variant="contained" color="error" onClick={handleClientDelete} disabled={saving}>
						Eliminar
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};
